/**
 * 成员建档（后台代成员开户）。
 *
 * ══ 为什么后台建档必须单独一页 ══
 *
 * C 端的注册路径是「凭邀请码自助开户」，开户时姓名只能写成占位符
 * （`name='新'` / `full_name='待完善档案'`）—— 那是「先开户、后补档案」的临时状态。
 * 但现实里大量成员是**线下入会**：秘书处拿到手机号与姓名，由员工代为建档。
 * 如果后台也走 C 端那条路，成员第一次登录会看到自己叫「待完善档案」。
 * 因此这一页把**真实姓名**作为必填项，服务端在建档后立刻覆盖占位符（有断言固定，见 verify-onboarding.ts）。
 *
 * 两条边界：
 *   1. 手机号已存在时服务端返回 **409**，界面原文显示，**绝不静默改写已有档案**；
 *   2. 守护员工与板块是可选项，但一旦指定就必须是真实在册员工 —— 选项来自接口，不手抄 id。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberLevels } from '@hew3/shared';
import { adminApi, ApiError } from '../lib/api.ts';
import type { StaffOptionsResult } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Card, PageHead, PermissionBlock } from '../components/ui.tsx';
import { ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import { scopeLabel } from '../lib/format.ts';

/**
 * 建档需要 `member:write`。
 *
 * 为什么在页面上也要拦一道（服务端已经会 403）：不拦的话，缺权限的角色直接访问
 * `/members/new` 会看到一个**填完必然失败**的表单 —— 用户以为功能可用，提交才被拒。
 * 这与 `AlertsPage` / `ReportsPage` 对 403 的处理方式也不一致。
 * 服务端仍是真正的关口，这里只是不把「不可用的功能」伪装成可用。
 */
export function MemberCreatePage({ permissions }: { permissions: string[] }) {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [level, setLevel] = useState<string>(memberLevels[0]?.key ?? 'standard');
  const [city, setCity] = useState('');
  const [company, setCompany] = useState('');
  const [referrer, setReferrer] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [boardStaffId, setBoardStaffId] = useState('');
  const [board, setBoard] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // 守护员工选项：读不到（403 / 网络失败）不影响建档本身 —— 该字段是可选，
  // 因此这里只提示「选项不可用」，不把整页变成错误态。
  const options = useLoad<StaffOptionsResult>((signal) => adminApi.staffOptions(signal));

  const boards = options.data?.boards ?? [];
  const effectiveBoard = board || options.data?.defaultBoard || '';

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.createMember({
        phone: phone.trim(),
        name: name.trim(),
        level,
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(company.trim() ? { company: company.trim() } : {}),
        ...(referrer.trim() ? { referrer: referrer.trim() } : {}),
        ...(inviteCode.trim() ? { inviteCode: inviteCode.trim().toUpperCase() } : {}),
        ...(boardStaffId ? { boardStaffId, board: effectiveBoard } : {}),
      });
      // 建档成功即进入该成员详情页：让操作者立刻看到「真的建出来了、姓名对不对」
      navigate(`/members/${encodeURIComponent(result.member.id)}`);
    } catch (err) {
      // 服务端原文照实呈现：409（手机号已存在）/ 403（邀请码失效或权限不足）/ 400（级别不一致）各自带着原因
      setError(err instanceof ApiError ? `建档被拒绝（${err.status}）：${err.message}` : '建档请求失败，请确认后端服务可用');
      setBusy(false);
    }
  };

  const phoneOk = /^1[3-9]\d{9}$/.test(phone.trim());
  const canSubmit = phoneOk && name.trim().length > 0 && !busy;

  // 权限守卫必须放在所有 hooks 之后（hooks 不能条件调用）
  if (!permissions.includes('member:write')) {
    return (
      <div>
        <PageHead title="成员建档" sub="线下入会由员工代为开户。" />
        <PermissionBlock
          title="当前角色没有「member:write」权限"
          desc="建档会创建新的成员档案并写入审计链，因此需要 member:write 权限点。具备该权限的是「超级管理员」与「运营」；数据运营、专家、财务、中台管理员不具备。这不是界面故障，是权限模型的正常结果 —— 服务端同样会拒绝该请求。"
        />
      </div>
    );
  }

  return (
    <div>
      <PageHead
        title="成员建档"
        sub="线下入会由员工代为开户：姓名是必填的真实姓名（服务端会覆盖「待完善档案」占位符），手机号重复会返回 409。"
      />

      {options.loading && <LoadingBox text="正在加载守护员工选项…" />}
      {options.error !== null && (
        <ErrorBox
          message={`守护员工选项读取失败：${errorText(options.error)}（该字段为可选，仍可继续建档）`}
          onRetry={options.reload}
        />
      )}

      <Card title="成员信息" sub="会籍级别决定成员在 C 端的身份口径，请与邀请码级别保持一致">
        <div className="grid2">
          <div className="field">
            <label className="field-label" htmlFor="nm-phone">
              手机号（11 位，登录身份，唯一）
            </label>
            <input
              id="nm-phone"
              className="field-input"
              inputMode="numeric"
              placeholder="13800000001"
              value={phone}
              maxLength={11}
              onChange={(e) => setPhone(e.target.value)}
            />
            {phone.length > 0 && !phoneOk && <div className="field-error">请输入有效的 11 位手机号</div>}
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-name">
              姓名（必填；不要留空，也不要用「待完善档案」这类占位）
            </label>
            <input
              id="nm-name"
              className="field-input"
              placeholder="例如：陈志远"
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-level">
              会籍级别
            </label>
            <select id="nm-level" className="field-input" value={level} onChange={(e) => setLevel(e.target.value)}>
              {memberLevels.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.name}（{l.key}）
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-city">
              城市（可选）
            </label>
            <input
              id="nm-city"
              className="field-input"
              placeholder="例如：北京"
              value={city}
              maxLength={20}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-company">
              企业（可选）
            </label>
            <input
              id="nm-company"
              className="field-input"
              placeholder="例如：××控股集团"
              value={company}
              maxLength={60}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-referrer">
              推荐来源（可选；不填则记「后台建档（员工录入）」）
            </label>
            <input
              id="nm-referrer"
              className="field-input"
              placeholder="例如：政商战略智库 · 推荐"
              value={referrer}
              maxLength={60}
              onChange={(e) => setReferrer(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-invite">
              邀请码（可选；填了就按「未作废 / 未过期 / 未用完 / 级别一致」逐项校验）
            </label>
            <input
              id="nm-invite"
              className="field-input"
              placeholder="HEW3-XXXXXXXX"
              value={inviteCode}
              maxLength={32}
              onChange={(e) => setInviteCode(e.target.value)}
            />
            <div className="field-hint">
              填了邀请码会消耗它的可用次数，并把它记为该成员的来源；不填则视为员工直接录入（成员档案里不记邀请码）。
            </div>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-staff">
              守护员工（可选；指派后该员工在成员列表里就能看到这位成员）
            </label>
            <select
              id="nm-staff"
              className="field-input"
              value={boardStaffId}
              onChange={(e) => setBoardStaffId(e.target.value)}
            >
              <option value="">暂不指派</option>
              {(options.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}（{s.no} · {s.roleName} · 可见范围 {scopeLabel(s.scope)} · 已守护 {s.guardianCount} 名）
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nm-board">
              守护板块（指定守护员工时生效）
            </label>
            <select
              id="nm-board"
              className="field-input"
              value={effectiveBoard}
              disabled={boardStaffId === ''}
              onChange={(e) => setBoard(e.target.value)}
            >
              {boards.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="field-error">{error}</div>}

        <div className="row">
          <button type="button" className="btn primary sm" disabled={!canSubmit} onClick={() => void submit()}>
            {busy ? '正在建档…' : '建档并进入详情页'}
          </button>
          <button type="button" className="btn sm" disabled={busy} onClick={() => navigate('/members')}>
            返回成员列表
          </button>
          <span className="text-tiny muted">
            建档会写入审计链（含工号）：成员在自己的「数据调用记录」里能看到是哪位员工为他建的档。
          </span>
        </div>
      </Card>

      <div className="note-src">
        <b>建档与自助注册的差别</b>
        <br />
        C 端自助注册只做「开户」（姓名暂为占位符，之后由成员本人完善）；后台建档则**必须写真实姓名**，
        因为这是员工替成员录入的正式档案。两条路径都会写入审计链，但操作者类型不同
        （员工建档 <span className="mono-num">actorType=staff</span> 带工号；自助注册
        <span className="mono-num">actorType=member</span>），成员侧可据此区分。
      </div>

      <div className="note-src">
        <b>为什么手机号重复必须报错</b>
        <br />
        手机号是成员的登录身份。若重复建档被静默允许，会出现两条共用一个登录身份的档案
        —— 后台看到两条、成员只看到一条，是最难排查的一类数据事故。因此服务端返回 409 并给出已有档案的编号，
        界面原文显示，请改去该成员的详情页修改资料。
      </div>
      <div className="sp8" />
    </div>
  );
}
